import { BundleUsage, PaymentType } from "@/pages/users/types/user";
import {
  NON_COUNTING_USAGE_STATUSES,
  collectUsagePayments,
  usageCountsTowardSessions,
} from "./user-calculations";
import {
  CompletedSessionProfessional,
  getCompletedSessionProfessionals,
  getUsageProductName,
} from "./past-professionals";

export interface ProductSummary {
  // Stable identity: product.id, else `bundle:<name>` for product-less
  // bundles. Grouping/React keys use this — the display name can be renamed
  // (or collide) without splitting or merging a product's history.
  productKey: string;
  productName: string;
  // used/total cover only usages whose sessions still belong to the user —
  // fully refunded / transferred / converted packs are skipped (they'd show
  // phantom "sessions left"). Their statuses land in nonCountingStatusLabels.
  used: number;
  total: number;
  // True once any usage in the group counts toward used/total. A group where
  // every pack was refunded/transferred/converted renders a status badge in
  // the Sessions tile instead of a meaningless 0/0.
  hasCountingUsage: boolean;
  // Badge labels ("Refunded" / "Transferred" / "Converted") for the group's
  // non-counting usages, deduped, first-seen order. Rendered in both cases:
  // alone when nothing counts, and beside the count in a mixed group so ops
  // can see why the total is lower than the pack list suggests.
  nonCountingStatusLabels: string[];
  // Current preferred professional from the most recent usage in the group
  // that has one (bundles_usage arrives sorted start_date desc).
  currentProfessionalName: string | null;
  // Strictly past professionals who actually held sessions on this product,
  // most recent first (merged across the product's usages, deduped by
  // professional). The product's current preferred professional is excluded —
  // per product, so someone current on product A still shows under product B.
  pastProfessionals: CompletedSessionProfessional[];
  // Net paid per currency: (amount − amount_refunded) / 100, purchases and
  // session fees (reschedule / no-show / late-cancel) alike. Empty when the
  // product has no payments (gifted/free bundles).
  payments: { currency: string; net: number }[];
  // Net paid per payment type per currency (main units), for the per-product
  // hover. Only non-zero entries; a purchase-only product has one entry.
  paymentBreakdown: ProductPaymentBreakdownEntry[];
  // One entry per refunded payment on this product (paise), for the Payments
  // tile's "Refunded" line and its per-product breakdown tooltip.
  refunds: ProductRefund[];
}

export interface ProductPaymentBreakdownEntry {
  type: PaymentType;
  currency: string;
  net: number; // main units, same convention as `payments`
}

export interface ProductRefund {
  currency: string;
  amountRefunded: number; // paise
  // Gross paid (paise). Full-vs-partial is derived as amountRefunded >= amount
  // — bundle status can't be used because partial refunds never update it
  // (only full refunds flip it to FULLY_REFUNDED).
  amount: number;
  refundStatus: string | null;
  refundInitiatedAt: string | null;
}

/**
 * Rolls up a user's bundle usages by product for the summary card. Grouped by
 * bundle.product.id (bundle name for older bundles without a product), with
 * product.name carried for display. First-seen order is preserved, so with
 * date-desc input the most recently purchased product comes first.
 */
export function buildProductSummaries(
  bundlesUsage: BundleUsage[],
): ProductSummary[] {
  const groups = new Map<
    string,
    ProductSummary & {
      paymentsByCurrency: Map<string, number>;
      // Keyed "<type>|<currency>" — flattened into paymentBreakdown at the end.
      paymentsByType: Map<string, ProductPaymentBreakdownEntry>;
      currentProfessionalId: string | null;
    }
  >();

  for (const usage of bundlesUsage || []) {
    // Group by product LINE (sub_category, e.g. "Dr. Madhura") so duration
    // variants that are separate Product rows ("Dr. Madhura - 30 minutes" /
    // "- 45 minutes") merge into one product. Fall back to the product name,
    // then the bundle name for product-less bundles.
    const productLine =
      usage.bundle?.product?.sub_category?.trim() ||
      getUsageProductName(usage);
    const productKey = productLine
      ? `line:${productLine}`
      : `bundle:${usage.bundle?.name ?? "—"}`;
    const productName = productLine ?? usage.bundle?.name ?? "—";

    let group = groups.get(productKey);
    if (!group) {
      group = {
        productKey,
        productName,
        used: 0,
        total: 0,
        hasCountingUsage: false,
        nonCountingStatusLabels: [],
        currentProfessionalName: null,
        currentProfessionalId: null,
        pastProfessionals: [],
        payments: [],
        paymentBreakdown: [],
        refunds: [],
        paymentsByCurrency: new Map(),
        paymentsByType: new Map(),
      };
      groups.set(productKey, group);
    }

    // Session counts skip refunded/transferred/converted packs — those
    // sessions aren't the user's to book. Payments, refunds, and past
    // professionals still accumulate below (the Payments tile's Refunded row
    // depends on the refunded pack's payment staying in the group).
    if (usageCountsTowardSessions(usage)) {
      group.used += usage.utilized_session_quantity || 0;
      group.total += usage.total_session_quantity || 0;
      group.hasCountingUsage = true;
    } else {
      // usageCountsTowardSessions returned false, so the status is a key of
      // the map; the fallback only satisfies the Partial<> type.
      const label = NON_COUNTING_USAGE_STATUSES[usage.status] ?? usage.status;
      if (!group.nonCountingStatusLabels.includes(label)) {
        group.nonCountingStatusLabels.push(label);
      }
    }

    if (!group.currentProfessionalName && usage.professional?.name) {
      group.currentProfessionalName = usage.professional.name;
      group.currentProfessionalId = usage.professional.id ?? null;
    }

    // Purchase + session fees (reschedule / no-show / late-cancel) — all
    // money collected on the usage counts toward the product's net, and each
    // type accumulates separately for the per-product hover breakdown.
    for (const entry of collectUsagePayments(usage)) {
      const net = entry.net / 100;
      group.paymentsByCurrency.set(
        entry.currency,
        (group.paymentsByCurrency.get(entry.currency) ?? 0) + net,
      );

      const typeKey = `${entry.type}|${entry.currency}`;
      const existing = group.paymentsByType.get(typeKey);
      if (existing) {
        existing.net += net;
      } else {
        group.paymentsByType.set(typeKey, {
          type: entry.type,
          currency: entry.currency,
          net,
        });
      }

      if (entry.amountRefunded > 0) {
        group.refunds.push({
          currency: entry.currency,
          amountRefunded: entry.amountRefunded,
          amount: entry.amount,
          refundStatus: entry.refundStatus,
          refundInitiatedAt: entry.refundInitiatedAt,
        });
      }
    }

    // Merge this usage's past professionals into the product group: session
    // counts add up and the first–last range widens across the product's
    // usages, so the entry reflects the whole working relationship.
    for (const professional of getCompletedSessionProfessionals(usage)) {
      const existing = group.pastProfessionals.find(
        (p) => p.key === professional.key,
      );
      if (!existing) {
        group.pastProfessionals.push({ ...professional });
      } else {
        existing.sessionCount += professional.sessionCount;
        if (professional.firstSessionAt < existing.firstSessionAt) {
          existing.firstSessionAt = professional.firstSessionAt;
        }
        if (professional.lastSessionAt > existing.lastSessionAt) {
          existing.lastSessionAt = professional.lastSessionAt;
        }
      }
    }
  }

  // Fixed display order for the hover breakdown; a fully refunded payment
  // (net 0) drops out — it shows under refunds instead.
  const TYPE_ORDER: PaymentType[] = [
    "PURCHASE",
    "RESCHEDULE_FEE",
    "NO_SHOW_FEE",
    "LATE_CANCEL_FEE",
  ];

  return [...groups.values()].map(
    ({ paymentsByCurrency, paymentsByType, currentProfessionalId, ...group }) => ({
      ...group,
      // Exclude the product group's current professional — the list is
      // strictly past. Group-scoped, so the same professional still appears
      // under other products where they only held past sessions.
      pastProfessionals: group.pastProfessionals
        .filter((p) => !currentProfessionalId || p.key !== currentProfessionalId)
        .sort((a, b) => b.lastSessionAt.localeCompare(a.lastSessionAt)),
      payments: [...paymentsByCurrency.entries()].map(([currency, net]) => ({
        currency,
        net,
      })),
      paymentBreakdown: [...paymentsByType.values()]
        .filter((entry) => entry.net !== 0)
        .sort(
          (a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type),
        ),
    }),
  );
}
